/* eslint-disable no-nested-ternary */

import { useSelector } from 'react-redux';
import { TealDiscountTag } from '@images';

import { Icon, Tooltip } from '@components';
import CurrencyAmount from '@components/currency-amount/CurrencyAmount';
import { RoleType } from '@features/team-management/constants';
import { TransactionStatus } from '@features/transactions/constants';
import {
  getQuickCollectInfo,
  getTransactionStatus
} from '@features/transactions/helper';
import { DEFAULT_CURRENCY_CONFIG } from '@hooks/useCurrency';
import { MemberTransactionType } from '@types';
import { getFormattedPhoneNumber } from '@utils/generic';

import FeeConfigAccordion from '../fee-config-accordion/FeeConfigAccordion';
import TransactionDetailsCard from '../transaction-details-card/TransactionDetailsCard';
import PaymentHistory from './PaymentHistory';

import theme from '../../../../../themes/colors';

interface QuickCollectProps {
  memberDetails: MemberTransactionType;
  branchId: string;
  orderId: string;
  paymentStatus?: string;
  paymentSettlementStatus?: string;
  leadId?: string;
}

const QuickCollect = ({
  memberDetails,
  branchId,
  orderId,
  paymentStatus = '',
  paymentSettlementStatus,
  leadId
}: QuickCollectProps) => {
  const { rolesAndPermissions, currencyConfig } = useSelector(
    (state: any) => state.user
  );

  const isDefaultCurrency =
    currencyConfig?.code === DEFAULT_CURRENCY_CONFIG.code;

  const isPaymentManager =
    rolesAndPermissions?.role === RoleType.PAYMENT_MANAGER;

  const transactionStatus = getTransactionStatus(
    memberDetails.status,
    memberDetails.settlement_status
  );

  const displayText =
    memberDetails.type === 'individual'
      ? memberDetails.group_id
        ? `${memberDetails.group_name} - ${memberDetails.note}`
        : memberDetails.note
      : memberDetails.group_name;

  const formattedTotalAmount =
    Number(memberDetails.total_amount) +
    Number(memberDetails.total_discount_amount);

  return (
    <>
      <div className="mx-auto flex flex-col items-center py-6">
        <Icon name="quick-collect" size="none" className="mb-4" />
        <p
          className="max-w-96 truncate text-20"
          data-tooltip-id="user-name-payment-details"
        >
          {transactionStatus === TransactionStatus.TO_SETTLE ||
          transactionStatus === TransactionStatus.SETTLED
            ? `Paid by ${memberDetails.name}`
            : `To be paid by ${memberDetails.name}`}
        </p>
        {memberDetails?.name.length > 20 && (
          <Tooltip
            id="user-name-payment-details"
            type="text"
            place="top"
            tooltipText={memberDetails?.name}
            className="max-w-78"
            variant="dark"
          />
        )}
        {!isPaymentManager && (
          <p className="max-w-full text-grey">
            {memberDetails?.member_phone_number
              ? getFormattedPhoneNumber(memberDetails?.member_phone_number)
              : ''}
            {memberDetails?.member_secondary_phone_number &&
              `, ${getFormattedPhoneNumber(
                memberDetails.member_secondary_phone_number
              )}`}
          </p>
        )}

        <div className="flex items-center justify-center text-xl font-normal leading-normal lg:text-xl xl:text-2xl 3xl:text-3xl">
          <CurrencyAmount
            value={Number(memberDetails.total_amount)}
            className="gap-1"
            symbolClass={
              isDefaultCurrency
                ? ''
                : '!text-lg lg:!text-md xl:!text-lg 3xl:!text-xl !font-medium'
            }
            valueClass="font-semibold"
          />
        </div>
        <div className="mt-2 flex min-w-35 items-center justify-center gap-2 rounded-lg bg-primary10 px-4 py-2.5 text-sm">
          {memberDetails.type === 'individual' ? (
            memberDetails.group_id ? (
              <Icon name="group-quick-collect" size="none" />
            ) : (
              <Icon name="lightning" stroke={theme.yellow40} size="none" />
            )
          ) : (
            <Icon name="users-gold" size="none" />
          )}
          <div
            className="max-w-40 truncate text-sm font-normal text-grey"
            data-tooltip-id="user-name-payment-details-tooltip"
          >
            {displayText}
          </div>
          <Tooltip
            id="user-name-payment-details-tooltip"
            type="text"
            tooltipText={displayText}
            className="max-w-78"
            variant="dark"
          />
        </div>
      </div>
      {(memberDetails.fee_amount_configuration ||
        memberDetails?.fine_amount !== '0' ||
        Number(memberDetails?.cgst_amount) > 0 ||
        Number(memberDetails?.sgst_amount) > 0 ||
        Number(memberDetails?.igst_amount) > 0) && (
        <FeeConfigAccordion
          feeConfig={memberDetails.fee_amount_configuration || []}
          className="mb-6"
          fineAmount={memberDetails.fine_amount}
          totalAmount={memberDetails.total_amount}
          subTotalAmount={memberDetails?.original_amount_requested}
          closedAmount={memberDetails?.closed_amount}
          gstInfo={{
            cgstPercent: memberDetails?.cgst_percentage,
            sgstPercent: memberDetails?.sgst_percentage,
            igstPercent: memberDetails?.igst_percentage,
            cgstAmount: memberDetails?.cgst_amount,
            sgstAmount: memberDetails?.sgst_amount,
            igstAmount: memberDetails?.igst_amount,
            totalGst: memberDetails?.total_gst
          }}
          discountAmount={memberDetails?.total_discount_amount}
        />
      )}
      <div>
        <div>
          <div className="w-full rounded-xl border border-grey40 p-6">
            <div>
              {memberDetails.total_discount_amount &&
                Number(memberDetails.total_discount_amount ?? 0) > 0 && (
                  <div className="flex flex-col">
                    <div className="mb-2 flex flex-row items-center justify-between">
                      <div className="text-sm leading-none tracking-normal text-grey">
                        Total Amount Requested
                      </div>
                      <div className="flex flex-row items-center">
                        <span className="text-cofeeNeutral100 mr-2 text-sm font-medium leading-5 tracking-normal">
                          <CurrencyAmount
                            value={Number(memberDetails.total_amount)}
                            className={`${
                              isDefaultCurrency ? 'gap-0.25' : 'gap-0.5'
                            }`}
                            symbolClass={`${
                              isDefaultCurrency
                                ? 'text-[0.95em] !font-normal'
                                : ''
                            }`}
                            valueClass=""
                          />
                        </span>
                        <span className="text-sm leading-[17px] tracking-normal text-grey line-through">
                          <CurrencyAmount
                            value={Number(formattedTotalAmount)}
                            className={`${
                              isDefaultCurrency ? 'gap-0.25' : 'gap-0.5'
                            }`}
                            symbolClass={`${
                              isDefaultCurrency
                                ? 'text-[0.95em] !font-normal'
                                : ''
                            }`}
                            valueClass=""
                          />
                        </span>
                      </div>
                    </div>
                    {/* {discount tag} */}
                    <div className="mb-3 ml-auto inline-flex flex-row items-center gap-1 rounded-sm bg-cofeeTeal50 px-2 py-[5px] text-sm leading-none tracking-normal text-cofeeTeal300">
                      <TealDiscountTag />
                      <span>
                        <CurrencyAmount
                          value={Number(memberDetails.total_discount_amount)}
                          className={`${
                            isDefaultCurrency ? 'gap-0.25' : 'gap-0.5'
                          }`}
                          symbolClass={`${
                            isDefaultCurrency
                              ? 'text-[0.95em] !font-normal'
                              : ''
                          }`}
                          valueClass=""
                        />
                      </span>
                      <span>discount applied!</span>
                    </div>
                  </div>
                )}
              {getQuickCollectInfo(memberDetails).map((paymentInfo) => (
                <TransactionDetailsCard
                  key={paymentInfo.title}
                  paymentDetails={paymentInfo}
                  transactionStatus={getTransactionStatus(
                    memberDetails.status,
                    memberDetails.settlement_status
                  )}
                />
              ))}
            </div>
          </div>
          {memberDetails.total_discount_amount &&
            Number(memberDetails.total_discount_amount ?? 0) > 0 && (
              <PaymentHistory
                branchId={branchId}
                orderId={orderId}
                paymentStatus={paymentStatus}
                paymentSettlementStatus={paymentSettlementStatus}
                leadId={leadId}
              />
            )}
          {/* Tags */}
          {memberDetails.order_tags && (
            <>
              <div className="my-2 text-base font-medium text-grey">#Tags</div>
              <div className="mt-4 flex flex-wrap gap-2">
                {Object.entries(memberDetails.order_tags).map(
                  ([tag, value]) => (
                    <div
                      key={tag}
                      className="rounded-full border border-grey40 px-4 py-2 text-sm font-medium text-content"
                    >
                      {tag}: {value}
                    </div>
                  )
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default QuickCollect;
